import { DeclineSurvey } from './DeclineSurvey';
import { type DeclineSurveyReason, type DeclineSurveyResponse, Status } from './types';
import { Box, Modal } from '../../../../../../mads';
import { Button } from '../../../../../../mads/Button';
import { useI18n } from '../../../../../../shared/I18n';
import { useTareData } from '../../../../../../shared/native-modules/tare/TareDataContext';
import { getEnvironmentConfig } from '../../../../config/urls';
import { useViewjobOneGraphModel } from '../../../../context/ViewJobDataContext';
import { useEmployerOutreachSource } from '../../../../hooks/useEmployerOutreachSource';
import { useCloseVJ } from '../../../../lib/useCloseVJ';
import { logEmployerOutreachSubmitDeclineReason } from '../../../../util/employerOutreachSignalsLoggingUtil';
import { callRezContactApi, callRezContactGetApi } from '../EmployerOutreach/utils/rezContactApi';

import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface DeclineSurveyPageProps {
    visible: boolean;
    onClose: () => void;
    jobKey: string;
}

const styles = StyleSheet.create({
    modalContainer: {
        flex: 1,
        backgroundColor: '#fff',
    },
    headerContainer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        padding: 8,
    },
});

export function DeclineSurveyPage({
    visible,
    onClose,
    jobKey,
}: DeclineSurveyPageProps): React.JSX.Element {
    const i18n = useI18n();
    const ogModel = useViewjobOneGraphModel();
    const tareData = useTareData();
    const insets = useSafeAreaInsets();
    const [status, setStatus] = useState<Status>(Status.Fetching);
    const [reasons, setReasons] = useState<DeclineSurveyReason[]>([]);
    const { outreachSource } = useEmployerOutreachSource();
    const closeVJ = useCloseVJ();

    const buildEnvironment = tareData?.appModel?.buildEnvironment;
    const environmentConfig = getEnvironmentConfig(buildEnvironment);
    const rezcowBaseUrl = environmentConfig.rezcowBaseUrl;

    const fetchDeclineReasons = useCallback(() => {
        setStatus(Status.Fetching);

        void (async (): Promise<void> => {
            try {
                const engagements = ogModel?.employerOutreachEngagement?.engagements;
                if (!engagements || engagements.length === 0) {
                    throw new Error('No engagement data found');
                }

                const invite = engagements[0];

                const params = new URLSearchParams({
                    rAKey: invite.recipient.id,
                    sAKey: invite.sender.id,
                    uid: invite.id,
                    h: invite.conversation.key,
                    ecmi: invite.conversation.initialMessage.id,
                    source: 'HOMEPAGE',
                });

                const result = await callRezContactGetApi<{
                    localizedReasons?: Record<string, string>;
                }>({
                    baseUrl: rezcowBaseUrl,
                    endpoint: '/api/v1/contacted/decline/reasons',
                    params,
                });

                if (result.success && result.data?.localizedReasons) {
                    const reasonsFormatted: DeclineSurveyReason[] = Object.entries(
                        result.data.localizedReasons,
                    ).map(([id, description]) => ({
                        id,
                        description,
                    }));
                    setReasons(reasonsFormatted);
                    setStatus(Status.Ready);
                } else {
                    onClose();
                }
            } catch (err) {
                console.error('[Decline Survey] Failed to fetch reasons:', err);
                onClose();
            }
        })();
    }, [ogModel, onClose, rezcowBaseUrl]);

    useEffect(() => {
        if (visible) {
            fetchDeclineReasons();
        }
    }, [visible, fetchDeclineReasons]);

    const handleClose = useCallback(() => {
        setStatus(Status.Fetching);
        setReasons([]);
        onClose();
        setTimeout(() => {
            void closeVJ();
        });
    }, [onClose, closeVJ]);

    const handleOnSubmit = useCallback(
        (response: DeclineSurveyResponse) => {
            setStatus(Status.Submitting);

            void (async (): Promise<void> => {
                try {
                    const engagements = ogModel?.employerOutreachEngagement?.engagements;
                    if (!engagements || engagements.length === 0) {
                        throw new Error('No engagement data found');
                    }

                    const result = await callRezContactApi({
                        rezcowBaseUrl,
                        endpoint: '/api/v1/contacted/decline/survey',
                        engagement: engagements[0],
                        additionalPayload: {
                            reason: response.reasonId,
                            comment: response.comment || null,
                        },
                    });

                    if (result.success) {
                        setStatus(Status.Complete);

                        // Log the submit decline reason interaction
                        logEmployerOutreachSubmitDeclineReason({
                            employerOutreachSource: outreachSource,
                            accountKey: ogModel?.accountKey,
                            ctk: ogModel?.ctk,
                            jobKey,
                            mobtk: ogModel?.mobvjtk,
                            isError: false,
                        });

                        setTimeout(() => {
                            closeVJ();
                        });
                        onClose();
                    } else {
                        setStatus(Status.Error);
                    }
                } catch (err: unknown) {
                    console.error('[Decline Survey] Failed to submit:', err);

                    // Log error if the submit decline reason flow fails
                    logEmployerOutreachSubmitDeclineReason({
                        employerOutreachSource: outreachSource,
                        accountKey: ogModel?.accountKey,
                        ctk: ogModel?.ctk,
                        jobKey,
                        mobtk: ogModel?.mobvjtk,
                        isError: true,
                    });

                    setStatus(Status.Error);
                }
            })();
        },
        [ogModel, rezcowBaseUrl, jobKey, onClose, outreachSource, closeVJ],
    );

    return (
        <Modal visible={visible} animationType="slide" onRequestClose={handleClose}>
            <Box sx={[styles.modalContainer, { paddingTop: insets.top + 40 }]}>
                <Box sx={styles.headerContainer}>
                    <Button variant="tertiary" onPress={handleClose} size="md">
                        {i18n.gettext('Close')}
                    </Button>
                </Box>
                <DeclineSurvey onSubmit={handleOnSubmit} reasons={reasons} status={status} />
            </Box>
        </Modal>
    );
}
